define('core/views/RichMediaView',[
    'jquery',
    'backbone',

    'core/utils',
    'core/mediaConfig',
    'core/views/RichMediaLinkView',
    'core/templates/postMedia',
    'core/templates/postMediaPlaceholder',
], function (
    $,
    Backbone,

    utils,
    mediaConfig,
    RichMediaLinkView,
    postMediaTemplate,
    postMediaPlaceholderTemplate
) {
    'use strict';

    var handler = utils.preventDefaultHandler;

    var classFromModelAttr = function ($el, model, attribute, cls) {
        $el[model.get(attribute) ? 'addClass' : 'removeClass'](cls);
    };

    return Backbone.View.extend({
        className: 'media-container',
        // Block of html that can be deferred and collapsed. Subclasses must
        // implement getMediaDimensions.
        events: {
            'click [data-action=expand]': 'handleExpand',
            'click [data-action=contract]': 'handleContract',
            'click [data-action=force-load]': 'handleForceLoad',
        },

        template: postMediaTemplate,

        initialize: function (options) {
            this.options = options;
            this.media = options.media;
            this.template = options.template || this.template;

            this.$linkEl = null;

            this.setupMode();

            // Bind model events
            this.listenTo(this.model, 'change:activated', this.applyState);
            this.listenTo(this.model, 'change:deferredHeight',
                this.onChangeDeferredHeight);
            this.listenTo(this.model, 'change:showButtons', this.updateElementClass);
            this.listenTo(this.model, 'change:deferred', this.render);

            // Change mode when use toggles setting
            this.listenTo(mediaConfig, 'change:collapsed', this.setupMode);
        },

        getMediaDimensions: function () {
            // Get the expected width and height of the media which will be
            // embedded. Values should be null or an integer.
            // These values are used to scale the content to fit the embed.
            // They should avoid performing much calculation; estimations are
            // acceptable and will be corrected when the content loads.

            return {
                width: null,
                height: null,
            };
        },

        getAvailableWidth: function () {
            // This value will be unavailable until posts are rendered
            return this.$el.parent().width() || mediaConfig.get('loadedThumbnailWidth');
        },

        updateDeferredHeight: function () {
            this.model.set('deferredHeight', this.calculateDeferredHeight());
        },

        calculateDeferredHeight: function () {
            // Set deferredHeight to a best estimate of the scaled height of
            // the scaled height of the embedded media.

            var dimensions = this.getMediaDimensions();

            var width = dimensions.width;
            var height = dimensions.height;

            // If width or height are missing, or if either is 0, we can't
            // scale the media dimensions.
            // If width is missing, we should use the exact height provided.
            // If both are missing, this will clear deferredHeight.
            if (!width || !height)
                return height;

            // We have a numeric width and height, can scale it to fit the
            // actual width we have available while maintaining aspect ratio.
            var availableWidth = this.getAvailableWidth();
            var expectedHeight = availableWidth * height / width;
            return expectedHeight;
        },

        convertToButton: function ($link) {
            // Converts $link into a RichMediaLinkView.
            this.model.set('showButtons', false);
            if (this.linkSubview)
                this.linkSubview.remove();
            this.linkSubview = new RichMediaLinkView({
                model: this.model,
                media: this.media,
                $link: $link,
            });
            $link.replaceWith(this.linkSubview.$el);
            this.linkSubview.render();
        },

        applyContentNodeHeight: function (height) {
            // Set the height of the contentNode, or clear it
            this.contentNode.height(height || 'auto');
        },

        shouldAutoplay: function () {
            // Content should autoplay when the user manually expanded it.
            // Since this function is only invoked when about to display the
            // content, we can check deferred to see if we're in collapsible
            // mode at all (in which case it should be auto played).
            return !this.model.get('deferred');
        },

        generateContentHtml: function () {
            // Returns the HTML of the content.
            // This is overridden in subclasses.

            return this.media.get('html');
        },

        createContentNode: function (html) {
            // Converts html to a DOM node.
            // This is overridden in subclasses.

            return $(html);
        },

        insertContentNode: function ($node) {
            // Replaces displayed content with $node.
            // This is overridden in subclasses.

            this.contentNode.html($node);
        },

        prepareElementEvents: function (/* $node */) {
            // Forward load and error events from the content element to the view.
            // This is overridden in subclasses.
        },

        displayContent: function () {
            // Create and display the rich media.
            this.updateDeferredHeight();
            var html = this.generateContentHtml();
            var $node = this.createContentNode(html);
            this.prepareElementEvents($node);
            this.insertContentNode($node);
            this.applyContentNodeHeight(null);
        },

        configureDeferred: function () {
            // rNEXT overwrites this function (see comment in rNEXT for why this function exists)
            this.enterViewport();
        },

        configureContentFromActivated: function () {
            if (this.model.get('activated'))
                this.displayContent();
            else
                this.displayPlaceholder();
        },

        displayPlaceholder: function () {
            this.contentNode.html(postMediaPlaceholderTemplate({
                model: this.model.toJSON(),
            }));
        },

        updateElementClass: function () {
            // Update element classes
            var $el = this.$el;
            var model = this.model;

            classFromModelAttr($el, model, 'deferred', 'media-mode-deferred');
            classFromModelAttr($el, model, 'activated', 'media-activated');
            classFromModelAttr($el, model, 'showButtons', 'media-show-buttons');
        },

        applyState: function () {
            this.configureDeferred();

            this.configureContentFromActivated();

            this.updateElementClass();
        },

        render: function () {
            this.$el.html(this.template({
                model: this.model.toJSON(),
                media: this.media.toJSON(),
            }));

            this.contentNode = this.$el.find('[data-role=content-placeholder]');

            // Will insert content (if necessary),
            // ensure correct buttons are displayed (if any),
            // and enable view tracking (if applicable)
            this.applyState();

            return this;
        },

        remove: function () {
            if (this.linkSubview)
                this.linkSubview.remove();

            Backbone.View.prototype.remove.apply(this, arguments);
        },

        enterViewport: function () {
            // Only trigger the load if we've entered view while deferred
            if (this.model.get('deferred'))
                this.activate();
        },

        activate: function () {
            this.model.set('activated', true);
        },

        setupMode: function () {
            if (!this.model.get('respectSettings'))
                return;

            // De-activate the view if necessary
            this.model.set('activated', false);

            var collapse = mediaConfig.get('collapsed');
            if (collapse)
                this.model.set('deferred', false);
            else
                this.model.set('deferred', true);
        },

        // Model Events
        onChangeDeferredHeight: function () {
            // Apply new deferred height to content if necessary
            if (this.model.get('deferred') && !this.model.get('activated'))
                this.applyContentNodeHeight(this.model.get('deferredHeight'));
        },

        // DOM Events
        handleExpand: handler(function () {
            this.model.set('activated', true);
        }),

        handleContract: handler(function () {
            this.model.set('activated', false);
        }),

        handleForceLoad: handler(function () {
            if (this.model.get('deferred'))
                this.model.set('activated', true);
        }),
    });
});

